<template>
  <div class="min-h-screen bg-gray-50">
    <AppHeader />
    
    <div class="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-4">Complete Your Payment</h1>
        <p class="text-lg text-gray-600">Secure payment processing for your order</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Order Summary -->
        <div class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>
          
          <div v-if="order" class="space-y-4">
            <div class="flex justify-between items-center pb-4 border-b border-gray-200">
              <div>
                <h3 class="font-medium text-gray-900">{{ order.service_name }}</h3>
                <p class="text-sm text-gray-600">Order #{{ order.order_number }}</p>
              </div>
              <div class="text-right">
                <p class="font-medium text-gray-900">${{ order.total_amount?.toLocaleString() }}</p>
                <p class="text-sm text-gray-600">{{ order.package_name || 'Custom Package' }}</p>
              </div>
            </div>
            
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Subtotal:</span>
                <span class="text-gray-900">${{ order.subtotal?.toLocaleString() || order.total_amount?.toLocaleString() }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Tax ({{ taxRate }}%):</span>
                <span class="text-gray-900">${{ calculateTax().toLocaleString() }}</span>
              </div>
              <div class="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
                <span>Total:</span>
                <span>${{ calculateTotal().toLocaleString() }}</span>
              </div>
            </div>
            
            <!-- Payment Schedule (if applicable) -->
            <div v-if="order.payment_schedule" class="mt-6 pt-6 border-t border-gray-200">
              <h4 class="font-medium text-gray-900 mb-3">Payment Schedule</h4>
              <div class="space-y-2">
                <div 
                  v-for="(payment, index) in order.payment_schedule" 
                  :key="index"
                  class="flex justify-between text-sm"
                  :class="index === 0 ? 'font-medium text-primary-600' : 'text-gray-600'"
                >
                  <span>{{ payment.description }}</span>
                  <span>${{ payment.amount.toLocaleString() }}</span>
                </div>
              </div>
              <p class="text-xs text-gray-500 mt-2">
                * You are paying the first installment now
              </p>
            </div>
          </div>
          
          <div v-else class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="text-gray-600 mt-2">Loading order details...</p>
          </div>
        </div>

        <!-- Payment Form -->
        <div class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">Payment Information</h2>
          
          <!-- Payment Method Selection -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">Payment Method</label>
            <div class="grid grid-cols-2 gap-4">
              <button
                @click="paymentMethod = 'card'"
                :class="paymentMethod === 'card' ? 'ring-2 ring-primary-500 border-primary-500' : 'border-gray-300'"
                class="flex items-center justify-center p-4 border rounded-lg hover:border-gray-400 transition-colors"
              >
                <svg class="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M2 10h20" stroke="currentColor" stroke-width="2"/>
                </svg>
                <span class="font-medium">Credit Card</span>
              </button>
              
              <button
                @click="paymentMethod = 'paypal'"
                :class="paymentMethod === 'paypal' ? 'ring-2 ring-primary-500 border-primary-500' : 'border-gray-300'"
                class="flex items-center justify-center p-4 border rounded-lg hover:border-gray-400 transition-colors"
              >
                <svg class="w-8 h-8 mr-3 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.26-.93 4.778-4.005 6.473-7.955 6.473h-2.05c-.262 0-.48.19-.523.45L8.824 20.9h4.907c.262 0 .48-.19.523-.45l.943-5.976h1.474c3.262 0 5.705-1.32 6.435-5.139.318-1.663.014-3.027-.96-4.018z"/>
                </svg>
                <span class="font-medium">PayPal</span>
              </button>
            </div>
          </div>

          <!-- Credit Card Form -->
          <div v-if="paymentMethod === 'card'" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
              <input
                v-model="cardForm.number"
                type="text"
                placeholder="1234 5678 9012 3456"
                maxlength="19"
                @input="formatCardNumber"
                class="form-input"
                required
              />
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input
                  v-model="cardForm.expiry"
                  type="text"
                  placeholder="MM/YY"
                  maxlength="5"
                  @input="formatExpiry"
                  class="form-input"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">CVC</label>
                <input
                  v-model="cardForm.cvc"
                  type="text"
                  placeholder="123"
                  maxlength="4"
                  class="form-input"
                  required
                />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
              <input
                v-model="cardForm.name"
                type="text"
                placeholder="John Doe"
                class="form-input"
                required
              />
            </div>
          </div>

          <!-- PayPal Info -->
          <div v-if="paymentMethod === 'paypal'" class="text-center py-8">
            <svg class="w-16 h-16 mx-auto text-blue-600 mb-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.26-.93 4.778-4.005 6.473-7.955 6.473h-2.05c-.262 0-.48.19-.523.45L8.824 20.9h4.907c.262 0 .48-.19.523-.45l.943-5.976h1.474c3.262 0 5.705-1.32 6.435-5.139.318-1.663.014-3.027-.96-4.018z"/>
            </svg>
            <p class="text-gray-600">You will be redirected to PayPal to complete your payment securely.</p>
          </div>

          <!-- Billing Address -->
          <div class="mt-6 pt-6 border-t border-gray-200">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Billing Address</h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  v-model="billingForm.address"
                  type="text"
                  placeholder="123 Main Street"
                  class="form-input"
                  required
                />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    v-model="billingForm.city"
                    type="text"
                    placeholder="New York"
                    class="form-input"
                    required
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    v-model="billingForm.zip"
                    type="text"
                    placeholder="10001"
                    class="form-input"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Button -->
          <div class="mt-8">
            <button
              @click="processPayment"
              :disabled="!canPay || processing"
              class="w-full btn btn-primary text-lg py-3"
            >
              <div v-if="processing" class="flex items-center justify-center">
                <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
              <span v-else>
                Pay ${{ calculateTotal().toLocaleString() }}
              </span>
            </button>
            
            <p class="text-xs text-gray-500 text-center mt-3">
              Your payment information is secure and encrypted. We use industry-standard security measures.
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orders'
import { usePaymentStore } from '@/stores/payments'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()
const paymentStore = usePaymentStore()
const authStore = useAuthStore()
const uiStore = useUIStore()

// State
const order = ref(null)
const paymentMethod = ref('card')
const processing = ref(false)
const taxRate = ref(8.25) // 8.25% tax rate

const cardForm = ref({
  number: '',
  expiry: '',
  cvc: '',
  name: ''
})

const billingForm = ref({
  address: '',
  city: '',
  zip: ''
})

// Computed
const canPay = computed(() => {
  if (!order.value) return false
  
  if (paymentMethod.value === 'card') {
    return cardForm.value.number && 
           cardForm.value.expiry && 
           cardForm.value.cvc && 
           cardForm.value.name &&
           billingForm.value.address &&
           billingForm.value.city &&
           billingForm.value.zip
  }
  
  if (paymentMethod.value === 'paypal') {
    return billingForm.value.address &&
           billingForm.value.city &&
           billingForm.value.zip
  }
  
  return false
})

// Methods
const calculateTax = () => {
  if (!order.value) return 0
  const subtotal = order.value.subtotal || order.value.total_amount || 0
  return Math.round(subtotal * (taxRate.value / 100) * 100) / 100
}

const calculateTotal = () => {
  if (!order.value) return 0
  const subtotal = order.value.subtotal || order.value.total_amount || 0
  return subtotal + calculateTax()
}

const formatCardNumber = (event) => {
  let value = event.target.value.replace(/\s/g, '').replace(/[^0-9]/gi, '')
  let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value
  if (formattedValue.length > 19) formattedValue = formattedValue.substr(0, 19)
  cardForm.value.number = formattedValue
}

const formatExpiry = (event) => {
  let value = event.target.value.replace(/\D/g, '')
  if (value.length >= 2) {
    value = value.substring(0, 2) + '/' + value.substring(2, 4)
  }
  cardForm.value.expiry = value
}

const processPayment = async () => {
  if (!canPay.value || processing.value) return
  
  processing.value = true
  
  try {
    const paymentData = {
      order_id: order.value.id,
      amount: calculateTotal(),
      currency: 'USD',
      payment_gateway: paymentMethod.value === 'card' ? 'stripe' : 'paypal',
      payment_method_details: paymentMethod.value === 'card' ? {
        card_number: cardForm.value.number.replace(/\s/g, ''),
        expiry_month: cardForm.value.expiry.split('/')[0],
        expiry_year: '20' + cardForm.value.expiry.split('/')[1],
        cvc: cardForm.value.cvc,
        cardholder_name: cardForm.value.name
      } : {},
      billing_address: {
        address: billingForm.value.address,
        city: billingForm.value.city,
        zip: billingForm.value.zip
      }
    }
    
    const result = await paymentStore.processPayment(paymentData)
    
    if (result && result.id) {
      uiStore.showNotification({
        type: 'success',
        title: 'Payment Successful',
        message: 'Your payment has been processed successfully!'
      })
      
      // Redirect to success page or order details
      router.push(`/orders/${order.value.id}/success`)
    } else {
      throw new Error('Payment processing failed')
    }
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Payment Failed',
      message: error.message || 'Payment processing failed. Please try again.'
    })
  } finally {
    processing.value = false
  }
}

// Initialize
onMounted(async () => {
  const orderId = route.params.id
  if (orderId) {
    try {
      order.value = await orderStore.fetchOrder(orderId)
      
      // Check if user can pay for this order
      if (!authStore.isAuthenticated) {
        router.push('/login')
        return
      }
      
      if (order.value.status === 'completed') {
        uiStore.showNotification({
          type: 'info',
          title: 'Order Already Paid',
          message: 'This order has already been completed.'
        })
        router.push('/orders')
        return
      }
      
      if (!['approved', 'quote_sent'].includes(order.value.status)) {
        uiStore.showNotification({
          type: 'warning',
          title: 'Payment Not Available',
          message: 'Payment is not available for this order yet.'
        })
        router.push('/orders')
        return
      }
    } catch (error) {
      uiStore.showNotification({
        type: 'error',
        title: 'Order Not Found',
        message: 'The requested order could not be found.'
      })
      router.push('/orders')
    }
  }
})
</script>
